import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useFeatureServices, useFaContent, useTrackingManager } from 'src/feature-app';
import { DpsData, EsOneformCoreServiceV1 } from 'src/feature-service/es-oneform-core-feature-service/types';
import { IdOrder, IdPerson, IdVehicle, IdDealer } from 'src/types';
import { CemVenta, CemPosventa } from 'src/forms';
import { GenericErrorLayer } from 'src/components/layers';
import { createPincodeEntity } from 'src/forms/dps-utils';
import dayjs from 'dayjs';
import { getDealerKvps, getInstallations } from 'src/bff';
import { DomainAttributesValues } from 'src';
import { LoadingEncuestas } from 'src/components';

export interface CemFormProps {
  idOrder?: IdOrder;
  idPerson: IdPerson;
  idVehicle: IdVehicle;
  idDealer?: IdDealer;
  dpsData: DpsData;
  dealerName?: string;
  dealerGet?: any;
}

export function CemController() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [usado, setUsado] = useState(false);
  const [posventa, setPosventa] = useState(false);
  const [venta, setVenta] = useState(false);
  const [dealerName, setDealerName] = useState<string>(null);
  const [dealer, setDealer] = useState(null);
  const [installations, setInstallations] = useState(null);
  const [dealerCode, setDealerCode] = useState(null);
  const [caducado, setCaducado] = useState(false);
  const today = dayjs(new Date());
  const dpsDataRef = useRef(null);
  const idPersonRef = useRef(null);
  const idVehicleRef = useRef(null);
  const idDealerRef = useRef(null);
  const idOrderRef = useRef(null);

  const oneFormService = useFeatureServices()['es:oneform-core'] as EsOneformCoreServiceV1;
  const dispatch = useDispatch();
  const faContent = useFaContent();

  const handleNext = (value: any) => { };
  const handleComplete = () => {
    const dpsData = oneFormService.getDpsData();

    if (dpsData.pincode === null || dpsData.pincode.noPincodeFound || dpsData.pincode.pincodeError) {
      handleError();
    } else if (dpsData.pincode.pincodeUsed) {
      setUsado(true);
      setLoading(false);
    } else {
      const idPerson = createPincodeEntity(dpsData, 'PERSON');
      const idVehicle = createPincodeEntity(dpsData, 'VEHICLE');
      const idDealer = createPincodeEntity(dpsData, 'DEALER');
      const idOrder = createPincodeEntity(dpsData, 'ORDER');

      dpsDataRef.current = dpsData;
      idPersonRef.current = idPerson;
      idVehicleRef.current = idVehicle;
      idDealerRef.current = idDealer;
      idOrderRef.current = idOrder;

      const formDate = dayjs(dpsData.env);

      if (!dpsData.env) {
        setCaducado(true);
      } else {
        if (
          (faContent.cem === 'POSVENTA' || faContent.encuesta === 'CEM - POSVENTA') &&
          today.diff(formDate, 'day') < 5
        ) {
          if (dpsData.dealer) {
            getDealerKvps(dpsData.dealer).then((dealer) => {
              setDealerName(dealer.name);
              setDealer(dealer);
              setPosventa(true);
            });
          } else {
            handleError();
          }
        } else if (
          (faContent.cem === 'VENTA' || faContent.encuesta === 'CEM - VENTA') &&
          today.diff(formDate, 'day') < 5
        ) {
          setDealerCode(idDealer.Dealer_Code_Vaesa__c);
          getInstallations('VW_DDB_FRM_LEM_REC_LEM').then((dealers) => {
            const installationsDomainAtt = dealers.map((inst) => inst.attributes);
            setInstallations(installationsDomainAtt);
            setVenta(true);
          });
        } else {
          setCaducado(true);
        }
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    if (installations) {
      let ok = false;
      let i = 0;
      let len = installations.length;
      while (i < len && ok === false) {
        const obj = installations[i].find((ele: DomainAttributesValues) => {
          if (ele.name === 'TXT_V_Installation_Code__c' && ele.value === dealerCode) {
            return ele;
          }
        });
        if (obj) {
          ok = true;
          setDealer(installations[i]);
        }
        i++;
      }
    }
  }, [installations]);

  const handleError = () => {
    setError(true);
    setLoading(false);
  };

  useEffect(() => {
    const oneFormCoreDpsIsLoaded = oneFormService.getLoadedState();

    oneFormCoreDpsIsLoaded.subscribe({
      next(value) {
        handleNext(value);
      },
      error(err) {
        console.error(err);
        handleError();
      },
      complete() {
        handleComplete();
      },
    });
  }, []);

  if (loading) {
    return <LoadingEncuestas />;
  } else if (error) {
    return (
      <>
        <GenericErrorLayer
          title="Uy, algo ha pasado"
          bodycopy="Por alguna razón no hemos podido cargar este cuestionario."
          ctaLayer="Reintentar"
          notShowCTA={true}
          icon={false}
          notALayer={true}
        />
      </>
    );
  } else if (caducado) {
    return (
      <>
        <GenericErrorLayer
          title="Uy, este formulario ha caducado"
          bodycopy="Parece que el cuestionario ya no está disponible. ¿Quieres contarnos cómo ha sido tu experiencia?"
          ctaLayer="Contacta con nosotros"
          ctaType="a"
          urlHref="https://formulario.volkswagen.es/sac?formid=vw-sac"
          icon={false}
          notALayer={true}
        />
      </>
    );
  } else if (usado) {
    return (
      <>
        <GenericErrorLayer
          title="Uy, ya has respondido a este cuestionario"
          bodycopy="Parece que ya has compartido tu opinión con nosotros. ¿Quieres añadir algo más?"
          ctaLayer="Contacta con nosotros"
          ctaType="a"
          urlHref="https://formulario.volkswagen.es/sac?formid=vw-sac"
          icon={false}
          notALayer={true}
        />
      </>
    );
  } else {
    return (
      <>
        {venta ? (
          <CemVenta
            dealerGet={dealer}
            dpsData={dpsDataRef.current}
            idDealer={idDealerRef.current}
            idOrder={idOrderRef.current}
            idPerson={idPersonRef.current}
            idVehicle={idVehicleRef.current}
          />
        ) : (
          <>
            {posventa ? (
              <CemPosventa
                dealerGet={dealer}
                dealerName={dealerName}
                dpsData={dpsDataRef.current}
                idVehicle={idVehicleRef.current}
                idOrder={idOrderRef.current}
                idPerson={idPersonRef.current}
              />
            ) : null}
          </>
        )}
      </>
    );
  }
}
